import { Box, Button, useMediaQuery, useTheme } from '@mui/material';
import React, { useState } from 'react';
import { useDownload } from '@europrocurement/l2d-modules/hooks';
import { FaOptionIcon } from '@europrocurement/l2d-icons';
import { useDocumentPreviewContext } from '@europrocurement/l2d-modules/modules/Dossiers/providers/DocumentPreviewContext';
import useDossierDocumentFilesApi from '@europrocurement/l2d-modules/modules/Dossiers/hooks/useDossierDocumentFilesApi';
import { useSnackbar } from 'notistack';
import useDossierFile from '@europrocurement/l2d-modules/modules/Dossiers/hooks/useDossierFile';
import useFileChecker from '@europrocurement/l2d-modules/modules/Dossiers/hooks/useFileChecker';
import DossierDocumentsValidationHeader from '../DossierDocumentsValidationHeader/DossierDocumentsValidationHeader';
import DocumentPreviewFooter from '../../DocumentPreviewFooter/DocumentPreviewFooter';
import DocumentViewer from '../../DocumentViewer/DocumentViewer';

const DocumentPreviewOverlayWithActions: React.FunctionComponent = function () {
    const { palette, breakpoints } = useTheme();
    const downMd = useMediaQuery(breakpoints.down('md'));
    const { documentPreview, setDocumentPreview } = useDocumentPreviewContext();
    const { download } = useDownload();
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    const { handleUpdateDosDocFile, handleTypageDosDocFileIaDoc } = useDossierFile({
        setIsSubmitting,
    });
    const { getDosDocFileContent } = useDossierDocumentFilesApi();

    const { displayFilesCheckerForm } = useFileChecker();
    const { enqueueSnackbar } = useSnackbar();
    // Listener to render document preview
    if (!documentPreview) {
        return null;
    }

    const { fileName, content } = documentPreview;

    const handleClose = () => {
        setDocumentPreview(undefined);
    };

    const handleDownload = () => {
        download(fileName, content);
    };

    const handleChangeDocumentType = async (fichierId: number, newTypeDocId: number) => {
        if (isSubmitting) {
            return;
        }
        const { success } = await handleUpdateDosDocFile(
            fichierId as number,
            {
                typeDocDossier: newTypeDocId,
            },
            true,
        );
        if (success) {
            enqueueSnackbar('Type de document mis à jour avec succès');
        }
    };

    const onDocumentStatusChange = async (fichierId: number, statusId: number) => {
        if (isSubmitting) {
            return;
        }
        const { success } = await handleUpdateDosDocFile(fichierId as number, { status: statusId });
        if (success) {
            enqueueSnackbar('Statut de document mis à jour avec succès');
        }
    };

    const handleDeliassage = async (fichierId: number) => {
        if (isSubmitting) {
            return;
        }

        const { files } = await handleUpdateDosDocFile(fichierId as number, {
            typageAuto: true,
            deliassageAuto: true,
        });

        if (files) {
            const filteredFiles = files.filter((f) => f.id !== fichierId);
            if (filteredFiles.length > 0) {
                // files checker modal
                displayFilesCheckerForm([{ createdFiles: filteredFiles }], getDosDocFileContent);
            } else {
                enqueueSnackbar('Nouveaux documents non détectés', {
                    variant: 'info',
                    autoHideDuration: 2000,
                });
            }
        }
    };

    return (
        <Box // Wrapper
            onClick={handleClose}
            sx={{ width: '100%', height: '100%', position: 'fixed', top: 0, left: 0 }}
        >
            <Box
                onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}
                sx={{
                    position: 'fixed',
                    top: '0',
                    right: '0',
                    maxWidth: downMd ? '100%' : '50%',
                    width: '100%',
                    height: '100vh',
                    background: palette.background.paper,
                    boxShadow: `1px 1px 15px 0px ${palette.grey[200]}`,
                    borderRadius: '12px',
                    zIndex: 1150,
                    display: 'flex',
                    flexDirection: 'column',
                    paddingTop: '72px', // minHeight of the user toolbar
                }}
            >
                <DossierDocumentsValidationHeader
                    onDocumentTypeChange={handleChangeDocumentType}
                    onDocumentStatusChange={onDocumentStatusChange}
                    onDocumentAutoType={handleTypageDosDocFileIaDoc}
                    onDocumentDeliassage={handleDeliassage}
                    isSubmitting={isSubmitting}
                    navigationBtn={
                        <Button
                            sx={{
                                display: 'flex',
                                gap: '0 8px',
                                alignItems: 'center',
                                color: palette.secondary.main,
                                minWidth: '25px',
                            }}
                            onClick={handleClose}
                        >
                            <FaOptionIcon
                                sx={{
                                    lineHeight: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    fontSize: '14px',
                                }}
                                icon={['fasl', 'close']}
                            />
                        </Button>
                    }
                />
                <Box
                    sx={{
                        margin: '0 30px',
                        border: `solid 1px ${palette.grey[300]}`,
                        boxShadow: `0px 2px 2.5px 0px ${palette.grey[300]}`,
                        height: '80%',
                        borderRadius: '6px',
                        overflow: 'hidden',
                    }}
                >
                    <DocumentViewer verticalScroll />
                </Box>
                <DocumentPreviewFooter onDownload={handleDownload} />
            </Box>
        </Box>
    );
};

export default DocumentPreviewOverlayWithActions;
